import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Grid,
  TextField,
  Button,
  Typography,
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress
} from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Autocomplete } from '@material-ui/lab';
import DeleteIcon from '@material-ui/icons/Delete';
import AddIcon from '@material-ui/icons/Add';
import PrintIcon from '@material-ui/icons/Print';
import SaveIcon from '@material-ui/icons/Save';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import MoreVertIcon from '@material-ui/icons/MoreVert';
import EditIcon from '@material-ui/icons/Edit';
import FileCopyIcon from '@material-ui/icons/FileCopy';
import EmailIcon from '@material-ui/icons/Email';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import { useReactToPrint } from 'react-to-print';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import DateFnsUtils from '@date-io/date-fns';
import {
  MuiPickersUtilsProvider,
  KeyboardDatePicker
} from '@material-ui/pickers';
import 'jspdf-autotable';
import { useSelector } from 'react-redux';
import CliniqueService from '../../services/CliniqueService';
import ProformaService from '../../services/ProformaService';
import generatePDFBon from '../../utils/factureUtils';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
  },
  header: {
    marginBottom: theme.spacing(4),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    borderRadius: theme.spacing(1),
  },
  formSection: {
    marginBottom: theme.spacing(3),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
  },
  tableContainer: {
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  },
  totalSection: {
    marginTop: theme.spacing(3),
    padding: theme.spacing(2),
    backgroundColor: theme.palette.grey[100],
    borderRadius: theme.spacing(1),
  },
  buttonGroup: {
    marginTop: theme.spacing(3),
    '& > *': {
      marginRight: theme.spacing(1),
    },
  },
  statusChip: {
    marginLeft: theme.spacing(1),
  },
  printContainer: {
    padding: theme.spacing(3),
    backgroundColor: 'white',
    minHeight: '29.7cm',
  },
  proformaHeader: {
    borderBottom: '2px solid #000',
    paddingBottom: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  proformaFooter: {
    marginTop: theme.spacing(4),
    paddingTop: theme.spacing(2),
    borderTop: '1px solid #ccc',
  },
  actionButton: {
    minWidth: '120px',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '400px',
  },
}));

// Fonction pour formater les nombres sans décimales inutiles
const formatNumber = (number) => {
  // Si c'est un nombre entier, retourner sans décimales
  if (Number.isInteger(Number(number))) {
    return Number(number).toString();
  }
  // Sinon, formater avec 2 décimales max
  return Number(number).toFixed(2).replace('.00', '');
};

// Fonction pour formater les prix (sans .00 pour les entiers)
const formatPrice = (price) => {
  const num = Number(price);
  if (Number.isInteger(num)) {
    return num.toString();
  }
  // Pour les nombres avec décimales, garder jusqu'à 2 décimales
  const formatted = num.toFixed(2);
  // Enlever les .00 à la fin
  return formatted.replace(/(\.0+|(?<=\.[0-9])0+)$/, '');
};

// Statuts des proformas
const PROFORMA_STATUS = {
  BROUILLON: 'BROUILLON',
  ENVOYE: 'ENVOYE',
  CONVERTI: 'CONVERTI',
  ANNULE: 'ANNULE'
};

// Options pour le menu d'actions
const ProformaFacture = () => {
  const classes = useStyles();
  const auth = useSelector((state) => state.auth);
  const componentRef = useRef();
  const [allProducts, setAllProducts] = useState([]);
  const [lignesProforma, setLignesProforma] = useState([]);
  const [openAddProduct, setOpenAddProduct] = useState(false);
  const [numeroProforma, setNumeroProforma] = useState('');
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [proformaId, setProformaId] = useState(null);
  const [proformaStatus, setProformaStatus] = useState(PROFORMA_STATUS.BROUILLON);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedProforma, setSelectedProforma] = useState(null);

  // États pour le formulaire d'ajout de produit
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [quantite, setQuantite] = useState(1);
  const [prixUnitaire, setPrixUnitaire] = useState(0);

  const PHARMACY_NAME = process.env.REACT_APP_PHARMACY_NAME;
  const OWNER_NAME = process.env.REACT_APP_OWNER_NAME;
  const ADDRESS_LINE1 = process.env.REACT_APP_ADDRESS_LINE1;
  const ADDRESS_LINE2 = process.env.REACT_APP_ADDRESS_LINE2;
  const BUSINESS_INFO = process.env.REACT_APP_BUSINESS_INFO;
  const ADDRESS_EMAIL = process.env.REACT_APP_ADDRESS_EMAIL || "";

  // Initialiser le numéro de proforma
  useEffect(() => {
    generateNumeroProforma();
    fetchProducts();
    // Charger les proformas existants
    fetchProformas();
  }, []);

  const fetchProformas = async () => {
    try {
      // Pourrait être utilisé pour afficher une liste de proformas existants
      const response = await ProformaService.getRecentProformas(5);
      console.log('Proformas récents:', response);
    } catch (error) {
      console.error('Erreur chargement proformas:', error);
    }
  };

  const generateNumeroProforma = () => {
    const date = new Date();
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const random = Math.floor(1000 + Math.random() * 9000);
    const numero = `PRO-${year}${month}${day}-${random}`;
    setNumeroProforma(numero);
  };

  const fetchProducts = async () => {
    try {
      const response = await CliniqueService.getAllProduitDistincts();
      setAllProducts(response.data);
    } catch (error) {
      console.error('Erreur lors du chargement des produits:', error);
      toast.error('Erreur lors du chargement des produits');
    }
  };

  const initialValues = {
    nomClient: '',
    adresse: '',
    telephone: '',
    email: '',
    conditions: 'Paiement à 30 jours',
    validite: '30 jours',
    remarques: '',
  };

  const validationSchema = Yup.object().shape({
    nomClient: Yup.string().required('Le nom du client est requis'),
    adresse: Yup.string(),
    telephone: Yup.string(),
    email: Yup.string().email('Email invalide'),
  });

  const handleChangeValue = async (product) => {
    if (!product || !product.nomProd) {
      setSelectedProduct(null);
      setPrixUnitaire(0);
      return;
    }
    
    setSelectedProduct(product);
    
    try {
      const response = await CliniqueService.getNomProd(product.nomProd);
      const data = response.data;
      if (data && data[0] && data[0].prixProd) {
        setPrixUnitaire(data[0].prixProd);
      } else {
        // Si le prix n'est pas trouvé, utiliser 0 ou un prix par défaut
        setPrixUnitaire(0);
        toast.warning(`Prix non trouvé pour ${product.nomProd}`);
      }
    } catch (error) {
      console.error("Erreur lors de la récupération du prix:", error);
      setPrixUnitaire(0);
      toast.error(`Erreur lors de la récupération du prix pour ${product.nomProd}`);
    }
  };

  const handleAddProduct = () => {
    if (!selectedProduct) {
      toast.error('Veuillez sélectionner un produit');
      return;
    }

    if (quantite <= 0) {
      toast.error('La quantité doit être supérieure à 0');
      return;
    }

    // Vérifier que le prix est valide
    if (prixUnitaire <= 0) {
      toast.error('Le prix unitaire doit être supérieur à 0');
      return;
    }

    const nouveauProduit = {
      id: Date.now(),
      codeProduit: selectedProduct.codeIdProd || selectedProduct.id || '',
      nomProduit: selectedProduct.nomProd,
      quantite: parseInt(quantite),
      prixUnitaire: prixUnitaire,
      total: quantite * prixUnitaire
    };

    setLignesProforma([...lignesProforma, nouveauProduit]);
    
    // Réinitialiser le formulaire
    setSelectedProduct(null);
    setQuantite(1);
    setPrixUnitaire(0);
    setOpenAddProduct(false);
    
    toast.success('Produit ajouté au proforma');
  };

  const handleRemoveProduct = (id) => {
    setLignesProforma(lignesProforma.filter(item => item.id !== id));
  };

  const calculateTotals = () => {
    // Calculer le total HT (sans TVA)
    const totalHT = lignesProforma.reduce((sum, item) => sum + item.total, 0);
    
    // TVA à 0%
    const tva = 0;
    const totalTTC = totalHT; // Le total TTC est égal au total HT sans TVA

    return {
      totalHT,
      tva,
      totalTTC,
    };
  };

  // Sauvegarder le proforma dans la base de données
  const handleSaveProforma = async (values) => {
    if (lignesProforma.length === 0) {
      toast.error('Ajoutez au moins un produit');
      return;
    }

    setSaving(true);

    const proformaData = {
      nomClient: values.nomClient,
      adresseClient: values.adresse,
      telephoneClient: values.telephone,
      emailClient: values.email,
      conditionsPaiement: values.conditions,
      validite: values.validite,
      remarques: values.remarques,
      lignes: lignesProforma.map(ligne => ({
        codeProduit: ligne.codeProduit,
        nomProduit: ligne.nomProduit,
        quantite: ligne.quantite,
        prixUnitaire: ligne.prixUnitaire,
        unite: 'unité',
        totalLigne: ligne.total
      })),
      speudo: auth.nom,
      tauxTVA: 0.00 // TVA à 0%
    };

    try {
      let response;
      
      if (editing && proformaId) {
        // Mettre à jour le proforma existant
        response = await ProformaService.updateProforma(proformaId, proformaData);
        toast.success('Proforma mis à jour avec succès');
      } else {
        // Créer un nouveau proforma
        response = await ProformaService.createProforma(proformaData);
        setProformaId(response.idProforma);
        setNumeroProforma(response.numeroProforma);
        setProformaStatus(PROFORMA_STATUS.BROUILLON);
        setEditing(true);
        toast.success('Proforma créé avec succès');
      }
      
      return response;
    } catch (error) {
      console.error('Erreur sauvegarde proforma:', error);
      toast.error(error.error || 'Erreur lors de la sauvegarde');
      throw error;
    } finally {
      setSaving(false);
    }
  };

  // Charger un proforma existant
  const handleLoadProforma = async (id) => {
    setLoading(true);
    try {
      const response = await ProformaService.getProformaById(id);
      const proforma = response.proforma;
      
      // Mettre à jour les états
      setProformaId(proforma.idProforma);
      setNumeroProforma(proforma.numero);
      setSelectedDate(new Date(proforma.dateProforma));
      setProformaStatus(proforma.statut);
      setEditing(true);
      
      // Mettre à jour les valeurs du formulaire
      const formValues = {
        nomClient: proforma.nomClient,
        adresse: proforma.adresseClient || '',
        telephone: proforma.telephoneClient || '',
        email: proforma.emailClient || '',
        conditions: proforma.conditionsPaiement || 'Paiement à 30 jours',
        validite: proforma.validite || '30 jours',
        remarques: proforma.remarques || '',
      };
      
      // Mettre à jour les lignes de produits
      const lignes = response.lignes.map(ligne => ({
        id: Date.now() + Math.random(),
        codeProduit: ligne.codeProduit,
        nomProduit: ligne.nomProduit,
        quantite: ligne.quantite,
        prixUnitaire: ligne.prixUnitaire,
        total: ligne.totalLigne
      }));
      
      setLignesProforma(lignes);
      
      toast.success('Proforma chargé avec succès');
      return formValues;
    } catch (error) {
      console.error('Erreur chargement proforma:', error);
      toast.error(error.error || 'Erreur lors du chargement');
      throw error;
    } finally {
      setLoading(false);
      handleMenuClose();
    }
  };

  // Supprimer le proforma
  const handleDeleteProforma = async () => {
    if (!proformaId) {
      toast.error('Aucun proforma à supprimer');
      return;
    }

    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce proforma ?')) {
      try {
        await ProformaService.deleteProforma(proformaId, {
          speudo: auth.nom,
          raison: 'Suppression manuelle'
        });
        
        handleNewProforma();
        toast.success('Proforma supprimé avec succès');
      } catch (error) {
        console.error('Erreur suppression proforma:', error);
        toast.error(error.error || 'Erreur lors de la suppression');
      } finally {
        handleMenuClose();
      }
    }
  };

  // Convertir en facture
  const handleConvertToFacture = async () => {
    if (!proformaId) {
      toast.error('Veuillez d\'abord sauvegarder le proforma');
      return;
    }

    try {
      const response = await ProformaService.convertToFacture(proformaId, {
        speudo: auth.nom
      });
      
      setProformaStatus(PROFORMA_STATUS.CONVERTI);
      toast.success('Proforma converti en facture avec succès');
      
      // Rediriger vers la facture ou afficher un message
      console.log('Facture créée:', response);
    } catch (error) {
      console.error('Erreur conversion:', error);
      toast.error(error.error || 'Erreur lors de la conversion');
    } finally {
      handleMenuClose();
    }
  };

  // Envoyer par email
  const handleSendByEmail = async () => {
    if (!proformaId) {
      toast.error('Veuillez d\'abord sauvegarder le proforma');
      return;
    }

    const email = prompt('Entrez l\'email du destinataire:');
    if (!email) return;

    try {
      await ProformaService.sendProformaByEmail(proformaId, {
        email,
        speudo: auth.nom,
        message: `Bonjour,\n\nVeuillez trouver ci-joint le proforma ${numeroProforma}.\n\nCordialement`
      });
      
      setProformaStatus(PROFORMA_STATUS.ENVOYE);
      toast.success('Proforma envoyé par email avec succès');
    } catch (error) {
      console.error('Erreur envoi email:', error);
      toast.error(error.error || 'Erreur lors de l\'envoi');
    } finally {
      handleMenuClose();
    }
  };

  // Dupliquer le proforma
  const handleDuplicateProforma = async () => {
    if (!proformaId) {
      toast.error('Veuillez d\'abord sauvegarder le proforma');
      return;
    }

    try {
      const response = await ProformaService.duplicateProforma(proformaId, {
        speudo: auth.nom,
        modifications: {
          nomClient: `Copie de ${document.getElementById('nomClient')?.value || 'Client'}`,
          dateProforma: new Date()
        }
      });
      
      // Charger le nouveau proforma
      await handleLoadProforma(response.idProforma);
      toast.success('Proforma dupliqué avec succès');
    } catch (error) {
      console.error('Erreur duplication:', error);
      toast.error(error.error || 'Erreur lors de la duplication');
    } finally {
      handleMenuClose();
    }
  };

  // Marquer comme annulé
  const handleCancelProforma = async () => {
    if (!proformaId) {
      toast.error('Aucun proforma à annuler');
      return;
    }

    if (window.confirm('Êtes-vous sûr de vouloir annuler ce proforma ?')) {
      try {
        await ProformaService.updateProformaStatus(proformaId, {
          statut: PROFORMA_STATUS.ANNULE,
          speudo: auth.nom,
          description: 'Annulation manuelle'
        });
        
        setProformaStatus(PROFORMA_STATUS.ANNULE);
        toast.success('Proforma annulé avec succès');
      } catch (error) {
        console.error('Erreur annulation:', error);
        toast.error(error.error || 'Erreur lors de l\'annulation');
      } finally {
        handleMenuClose();
      }
    }
  };

  // Nouveau proforma
  const handleNewProforma = () => {
    if (lignesProforma.length > 0 && !window.confirm('Les modifications non sauvegardées seront perdues. Continuer ?')) {
      return;
    }
    
    generateNumeroProforma();
    setLignesProforma([]);
    setSelectedDate(new Date());
    setProformaId(null);
    setProformaStatus(PROFORMA_STATUS.BROUILLON);
    setEditing(false);
    
    // Réinitialiser le formulaire
    if (document.querySelector('form')) {
      document.querySelector('form').reset();
    }
    
    toast.success('Nouveau proforma créé');
  };

  // Gestion du menu d'actions
  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  // Générer PDF avec le modèle existant
  const generateProformaPDF = () => {
    if (lignesProforma.length === 0) {
      toast.error('Ajoutez au moins un produit');
      return;
    }

    // Préparer les données pour le modèle de PDF
    const proformaData = {
      reportProd: lignesProforma.map((ligne, index) => ({
        id: index + 1,
        nomProd: ligne.nomProduit,
        qtVdu: ligne.quantite,
        prixProdVendu: ligne.prixUnitaire,
        total: ligne.total,
        dateEnreg: selectedDate,
        beneficiaire: '', // Champ optionnel
        nom: document.getElementById('nomClient')?.value || 'Client',
        adresse: document.getElementById('adresse')?.value || '',
        telephone: document.getElementById('telephone')?.value || '',
        numFactSrt: numeroProforma || `PRO-${Date.now()}`,
        vendeur: auth.nom,
        statut: 'PROFORMA',
        remise: 0,
        tva: 0 // TVA à 0%
      })),
      clientInfo: {
        nom: document.getElementById('nomClient')?.value || 'Client',
        designation: 'PROFORMA'
      }
    };

    // Utiliser votre fonction de génération PDF existante
    generatePDFBon(proformaData.reportProd, proformaData.clientInfo);
  };

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Proforma-${numeroProforma}`,
  });

  const renderPrintContent = () => {
    const totals = calculateTotals();
    const nomClient = document.getElementById('nomClient')?.value || 'Client';
    const adresse = document.getElementById('adresse')?.value || '';
    const telephone = document.getElementById('telephone')?.value || '';
    const email = document.getElementById('email')?.value || '';
    const conditions = document.getElementById('conditions')?.value || 'Paiement à 30 jours';
    const validite = document.getElementById('validite')?.value || '30 jours';
    const remarques = document.getElementById('remarques')?.value || '';

    return (
      <div ref={componentRef} className={classes.printContainer}>
        <div className={classes.proformaHeader}>
          <Typography variant="h4" align="center" gutterBottom>
            PROFORMA
          </Typography>
          <Grid container justifyContent="space-between">
            <Grid item>
              <Typography variant="body1">
                <strong>N°:</strong> {numeroProforma}
              </Typography>
              <Typography variant="body1">
                <strong>Date:</strong> {selectedDate.toLocaleDateString()}
              </Typography>
              <Typography variant="body1">
                <strong>Statut:</strong> {proformaStatus}
              </Typography>
            </Grid>
            <Grid item>
              <Typography variant="body1">
                <strong>Page:</strong> 1/1
              </Typography>
              <Typography variant="body1">
                <strong>Vendeur:</strong> {auth.nom}
              </Typography>
            </Grid>
          </Grid>
        </div>

        <Grid container spacing={3} style={{ marginBottom: 20 }}>
          <Grid item xs={6}>
            <Typography variant="h6" gutterBottom>
              Informations Société
            </Typography>
            <Typography variant="body1">{PHARMACY_NAME}</Typography>
            <Typography variant="body1">{OWNER_NAME}</Typography>
            <Typography variant="body1">{ADDRESS_LINE1}</Typography>
            <Typography variant="body1">{BUSINESS_INFO}</Typography>
            {ADDRESS_EMAIL && <Typography variant="body1">{ADDRESS_EMAIL}</Typography>}
          </Grid>
          <Grid item xs={6}>
            <Typography variant="h6" gutterBottom>
              Client
            </Typography>
            <Typography variant="body1">{nomClient}</Typography>
            <Typography variant="body1">{adresse}</Typography>
            <Typography variant="body1">{telephone}</Typography>
            <Typography variant="body1">{email}</Typography>
          </Grid>
        </Grid>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell><strong>Produit</strong></TableCell>
                <TableCell align="right"><strong>Quantité</strong></TableCell>
                <TableCell align="right"><strong>Prix Unitaire HT</strong></TableCell>
                <TableCell align="right"><strong>Total HT</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {lignesProforma.map((ligne) => (
                <TableRow key={ligne.id}>
                  <TableCell>{ligne.nomProduit}</TableCell>
                  <TableCell align="right">{ligne.quantite}</TableCell>
                  <TableCell align="right">{formatPrice(ligne.prixUnitaire)} F</TableCell>
                  <TableCell align="right">{formatPrice(ligne.total)} F</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell colSpan={3} align="right"><strong>Total HT</strong></TableCell>
                <TableCell align="right"><strong>{formatPrice(totals.totalHT)} F</strong></TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={3} align="right"><strong>TVA (0%)</strong></TableCell>
                <TableCell align="right"><strong>0 F</strong></TableCell>
              </TableRow>
              <TableRow>
                <TableCell colSpan={3} align="right"><strong>TOTAL</strong></TableCell>
                <TableCell align="right">
                  <strong>{formatPrice(totals.totalTTC)} F</strong>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>

        <div className={classes.proformaFooter}>
          <Typography variant="body2" gutterBottom>
            <strong>Conditions:</strong> {conditions}
          </Typography>
          <Typography variant="body2" gutterBottom>
            <strong>Validité:</strong> {validite}
          </Typography>
          {remarques && (
            <Typography variant="body2" gutterBottom>
              <strong>Remarques:</strong> {remarques}
            </Typography>
          )}
          <Typography variant="body2">
            <strong>Note:</strong> Cette facture proforma ne constitue pas une facture définitive
          </Typography>
          <Box mt={4}>
            <Grid container justifyContent="space-between">
              <Grid item>
                <Typography variant="body2">
                  Signature Client
                </Typography>
                <Box height={50} borderBottom="1px solid #000" width={200} />
              </Grid>
              <Grid item>
                <Typography variant="body2">
                  Signature et Cachet
                </Typography>
                <Box height={50} borderBottom="1px solid #000" width={200} />
              </Grid>
            </Grid>
          </Box>
        </div>
      </div>
    );
  };

  const totals = calculateTotals();

  const getStatusColor = (status) => {
    switch (status) {
      case PROFORMA_STATUS.BROUILLON: return 'default';
      case PROFORMA_STATUS.ENVOYE: return 'primary';
      case PROFORMA_STATUS.CONVERTI: return 'success';
      case PROFORMA_STATUS.ANNULE: return 'error';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" className={classes.root}>
        <div className={classes.loadingContainer}>
          <CircularProgress size={60} />
          <Typography variant="h6" style={{ marginLeft: 16 }}>
            Chargement du proforma...
          </Typography>
        </div>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" className={classes.root}>
      <Paper className={classes.header} elevation={3}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <div>
            <Typography variant="h4" gutterBottom>
              Facture Proforma
            </Typography>
            <Typography variant="body1">
              Numéro: {numeroProforma}
              {proformaStatus && (
                <Chip
                  label={proformaStatus}
                  color={getStatusColor(proformaStatus)}
                  size="small"
                  className={classes.statusChip}
                />
              )}
            </Typography>
          </div>
          <div>
            <IconButton onClick={handleMenuOpen} color="inherit">
              <MoreVertIcon />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              keepMounted
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
            >
              {proformaId && (
                <MenuItem onClick={handleLoadProforma.bind(null, proformaId)}>
                  <ListItemIcon>
                    <EditIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Charger" />
                </MenuItem>
              )}
              <MenuItem onClick={handleDuplicateProforma}>
                <ListItemIcon>
                  <FileCopyIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Dupliquer" />
              </MenuItem>
              {proformaId && proformaStatus === PROFORMA_STATUS.BROUILLON && (
                <MenuItem onClick={handleSendByEmail}>
                  <ListItemIcon>
                    <EmailIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Envoyer par email" />
                </MenuItem>
              )}
              {proformaId && proformaStatus === PROFORMA_STATUS.BROUILLON && (
                <MenuItem onClick={handleConvertToFacture}>
                  <ListItemIcon>
                    <CheckCircleIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Convertir en facture" />
                </MenuItem>
              )}
              {proformaId && proformaStatus === PROFORMA_STATUS.BROUILLON && (
                <MenuItem onClick={handleCancelProforma}>
                  <ListItemIcon>
                    <CancelIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Annuler" />
                </MenuItem>
              )}
              {proformaId && (
                <MenuItem onClick={handleDeleteProforma}>
                  <ListItemIcon>
                    <DeleteIcon fontSize="small" />
                  </ListItemIcon>
                  <ListItemText primary="Supprimer" />
                </MenuItem>
              )}
            </Menu>
          </div>
        </Box>
      </Paper>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values) => {
          try {
            await handleSaveProforma(values);
          } catch (error) {
            // Erreur déjà gérée dans handleSaveProforma
          }
        }}
        enableReinitialize
      >
        {(props) => (
          <Form>
            <Paper className={classes.formSection} elevation={1}>
              <Typography variant="h6" gutterBottom>
                Informations Générales
              </Typography>
              <MuiPickersUtilsProvider utils={DateFnsUtils}>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <KeyboardDatePicker
                      margin="normal"
                      id="date-proforma"
                      label="Date du Proforma"
                      format="dd/MM/yyyy"
                      value={selectedDate}
                      onChange={(date) => setSelectedDate(date)}
                      KeyboardButtonProps={{
                        'aria-label': 'change date',
                      }}
                      fullWidth
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Numéro Proforma"
                      value={numeroProforma}
                      disabled
                      variant="outlined"
                    />
                  </Grid>
                </Grid>
              </MuiPickersUtilsProvider>
            </Paper>

            <Paper className={classes.formSection} elevation={1}>
              <Typography variant="h6" gutterBottom>
                Informations Client
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="nomClient"
                    id="nomClient"
                    label="Nom du Client"
                    fullWidth
                    variant="outlined"
                    error={props.errors.nomClient && props.touched.nomClient}
                    helperText={<ErrorMessage name="nomClient" />}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="adresse"
                    id="adresse"
                    label="Adresse"
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="telephone"
                    id="telephone"
                    label="Téléphone"
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="email"
                    id="email"
                    label="Email"
                    fullWidth
                    variant="outlined"
                    error={props.errors.email && props.touched.email}
                    helperText={<ErrorMessage name="email" />}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Paper className={classes.formSection} elevation={1}>
              <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Produits
                </Typography>
                <Button
                  variant="contained"
                  color="primary"
                  startIcon={<AddIcon />}
                  onClick={() => setOpenAddProduct(true)}
                  disabled={proformaStatus === PROFORMA_STATUS.CONVERTI || proformaStatus === PROFORMA_STATUS.ANNULE}
                >
                  Ajouter Produit
                </Button>
              </Box>

              <TableContainer className={classes.tableContainer}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Produit</TableCell>
                      <TableCell align="right">Quantité</TableCell>
                      <TableCell align="right">Prix Unitaire HT</TableCell>
                      <TableCell align="right">Total HT</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {lignesProforma.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          Aucun produit ajouté
                        </TableCell>
                      </TableRow>
                    ) : (
                      lignesProforma.map((ligne) => (
                        <TableRow key={ligne.id}>
                          <TableCell>{ligne.nomProduit}</TableCell>
                          <TableCell align="right">{ligne.quantite}</TableCell>
                          <TableCell align="right">{formatPrice(ligne.prixUnitaire)} F</TableCell>
                          <TableCell align="right">{formatPrice(ligne.total)} F</TableCell>
                          <TableCell align="center">
                            <IconButton
                              size="small"
                              onClick={() => handleRemoveProduct(ligne.id)}
                              disabled={proformaStatus === PROFORMA_STATUS.CONVERTI || proformaStatus === PROFORMA_STATUS.ANNULE}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <Paper className={classes.totalSection} elevation={0}>
                <Grid container spacing={2} justifyContent="flex-end">
                  <Grid item xs={12} sm={6} md={4}>
                    <Table size="small">
                      <TableBody>
                        <TableRow>
                          <TableCell><strong>Total HT</strong></TableCell>
                          <TableCell align="right">{formatPrice(totals.totalHT)} F</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>TVA (0%)</strong></TableCell>
                          <TableCell align="right">0 F</TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell><strong>TOTAL</strong></TableCell>
                          <TableCell align="right">
                            <Typography variant="h6" color="primary">
                              {formatPrice(totals.totalTTC)} F
                            </Typography>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </Grid>
                </Grid>
              </Paper>
            </Paper>

            <Paper className={classes.formSection} elevation={1}>
              <Typography variant="h6" gutterBottom>
                Conditions et Remarques
              </Typography>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="conditions"
                    id="conditions"
                    label="Conditions de paiement"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={2}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Field
                    as={TextField}
                    name="validite"
                    id="validite"
                    label="Validité"
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="remarques"
                    id="remarques"
                    label="Remarques"
                    fullWidth
                    variant="outlined"
                    multiline
                    rows={3}
                  />
                </Grid>
              </Grid>
            </Paper>

            <Box className={classes.buttonGroup}>
              <Button
                variant="contained"
                color="primary"
                startIcon={<SaveIcon />}
                type="submit"
                disabled={lignesProforma.length === 0 || saving || proformaStatus === PROFORMA_STATUS.CONVERTI || proformaStatus === PROFORMA_STATUS.ANNULE}
                className={classes.actionButton}
              >
                {saving ? (
                  <>
                    <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
                    Sauvegarde...
                  </>
                ) : editing ? (
                  'Mettre à jour'
                ) : (
                  'Sauvegarder'
                )}
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<PrintIcon />}
                onClick={handlePrint}
                disabled={lignesProforma.length === 0}
                className={classes.actionButton}
              >
                Imprimer
              </Button>
              <Button
                variant="contained"
                style={{ backgroundColor: '#d32f2f', color: 'white' }}
                startIcon={<PictureAsPdfIcon />}
                onClick={generateProformaPDF}
                disabled={lignesProforma.length === 0}
                className={classes.actionButton}
              >
                Générer PDF
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={handleNewProforma}
                className={classes.actionButton}
                disabled={saving}
              >
                Nouveau
              </Button>
              {proformaId && proformaStatus === PROFORMA_STATUS.BROUILLON && (
                <Button
                  variant="contained"
                  style={{ backgroundColor: '#4caf50', color: 'white' }}
                  startIcon={<CheckCircleIcon />}
                  onClick={handleConvertToFacture}
                  className={classes.actionButton}
                >
                  Convertir
                </Button>
              )}
            </Box>
          </Form>
        )}
      </Formik>

      {/* Dialog pour ajouter un produit */}
      <Dialog
        open={openAddProduct}
        onClose={() => setOpenAddProduct(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ajouter un produit</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Autocomplete
                options={allProducts}
                getOptionLabel={(option) => option.nomProd}
                value={selectedProduct}
                onChange={(event, newValue) => {
                    if (newValue) {
                      // Passer l'objet complet, pas juste le nom
                      handleChangeValue(newValue);
                    } else {
                      setSelectedProduct(null);
                      setPrixUnitaire(0);
                    }
                  }}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Rechercher un produit"
                    variant="outlined"
                    fullWidth
                  />
                )}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Quantité"
                type="number"
                value={quantite}
                onChange={(e) => setQuantite(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{ inputProps: { min: 1 } }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Prix Unitaire HT"
                type="number"
                value={prixUnitaire}
                onChange={(e) => setPrixUnitaire(e.target.value)}
                fullWidth
                variant="outlined"
                InputProps={{ inputProps: { min: 0, step: 1 } }} // step=1 pour des valeurs entières
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAddProduct(false)} color="primary">
            Annuler
          </Button>
          <Button onClick={handleAddProduct} color="primary" variant="contained">
            Ajouter
          </Button>
        </DialogActions>
      </Dialog>

      {/* Contenu caché pour l'impression */}
      <div style={{ display: 'none' }}>
        {renderPrintContent()}
      </div>
    </Container>
  );
};

export default ProformaFacture;